import NiceModal from "@ebay/nice-modal-react";
import ButtonEclipse from "components/buttons/ButtonEclipse";
import InputNumber from "components/inputs/InputNumber";
import FlexChild from "layouts/flex/FlexChild";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState, useContext } from "react";
import VerticalFlex from "layouts/flex/VerticalFlex";
import PaddingWrapper from "layouts/wrapper/PaddingWrapper";
import { validateInputs } from "shared/utils/Utils";
import { ToastContainer, toast } from "react-toastify";
import SquareWrapper from "layouts/wrapper/SquareWrapper";
import MobileAddCartBase from "modals/base/MobileAddCartBase";
import InputNumberMobile from "components/inputs/InputNumberMobile";
import { AuthContext } from "providers/AuthProvider";
import { storeRequester } from "App";
import { useNavigate } from "react-router-dom";
import { getCookie } from "shared/StoreRequester";
import style from "./AddCartCountModal.module.css";
import P from "components/P";
import { RegionContext } from "providers/RegionProvider";


const MobileAddCartModal = NiceModal.create((props, ref) => {
    const { t } = useTranslation();
    const [withHeader, withFooter] = [true, true];
    const [width, height] = ["min(100%, 768px)", "max(92%, calc(100vh - 50px))"];
    const withCloseButton = false;
    const clickOutsideToClose = true;
    const buttonText = t("close");
    const modal = useRef();
    const navigate = useNavigate();
    const modalContentRef = useRef();
    const inputs = useRef([]);
    const { product } = props;

    const [items, setItems] = useState([]);
    const [isIncrease, setIsIncrease] = useState(false);
    const [variantQuantities, setVariantQuantities] = useState({});
    const { userName } = useContext(AuthContext);
    const { regionId } = useContext(RegionContext);
    const [cartItems, setCartItems] = useState([]);
    const title = "장바구니담기";

    useEffect(() => {
        // 모달이 열렸을 때 body 스크롤 비활성화
        document.body.style.overflow = "hidden";

        // 모달이 닫힐 때 복구
        return () => {
            document.body.style.overflow = "";
        };
    }, [])

    useEffect(() => {
        const fetchCart = async () => {
            const { cart } = await storeRequester.getMyCart(regionId);
            setCartItems(cart?.items || []);
        };
        fetchCart();
    }, [regionId]);

    const calculateRemainingInventory = (variantId) => {
        const cartItem = cartItems.find(item => item.variant_id === variantId);
        const cartQuantity = cartItem?.quantity || 0;

        const variant = product.variants
            ? product.variants.find(v => v.id === variantId)
            : product;
        if (!variant) return 0;

        const remainingQuantity = variant.inventory_quantity - cartQuantity;
        return remainingQuantity > 0 ? remainingQuantity : 0;
    };

    // 재고 검증 함수
    const validateInventory = (items) => {
        for (const item of items) {
            const cartItem = cartItems.find(ci => ci.variant_id === item.variant_id);
            const cartQuantity = cartItem?.quantity || 0;

            const variant = product.variants
                ? product.variants.find(v => v.id === item.variant_id)
                : product;
            if (!variant) return false;

            if ((cartQuantity + item.quantity) > variant.inventory_quantity) {
                toast.error(`${variant.title}의 재고가 부족합니다.`, {
                    autoClose: 1000,
                    position: toast.POSITION.BOTTOM_CENTER,
                });
                return false;
            }
        }
        return true;
    };

    // 가격 계산 함수
    const calculatePrice = (variant) => {
        if (!userName) return "-"
        const quantity = variantQuantities[variant.id] || 0;
        const priceToUse = variant.discount_price || variant.price;
        return Math.floor(priceToUse * quantity);
    };

    // 총액 계산 함수
    const calculateTotalPrice = () => {
        if (!userName) return "-"
        if (product.variants && product.variants.length > 0) {
            return product.variants.reduce((total, variant) => {
                const quantity = variantQuantities[variant.id] || 0;
                const priceToUse = variant.discount_price || variant.price;
                return total + Math.floor(priceToUse * quantity);
            }, 0);
        } else {
            const quantity = variantQuantities[product.id] || 0;
            const priceToUse = product.discount_price || product.price;
            const total = Math.floor(priceToUse * quantity)
            return total;
        }

    };

    // 수량 변경 핸들러
    const handleInputChange = (variantId, index, quantity) => {
        // 재고 체크
        const variant = product.variants?.find(v => v.id === variantId) || product;
        const remainingInventory = calculateRemainingInventory(variantId);

        // 재고가 없거나 sold out인 경우
        if (remainingInventory === 0 || variant.sold === true) {
            toast.error(t("재고를 확인해주세요"), {
                autoClose: 1000,
                position: toast.POSITION.BOTTOM_CENTER,
            });
            inputs.current[index].setValue(0);
            setVariantQuantities(prev => {
                const newState = { ...prev };
                delete newState[variantId];
                return newState;
            });
            setItems(prevItems => prevItems.filter(item => item.variant_id !== variantId));
            return;
        }

        // 재고보다 많은 수량을 선택한 경우
        if (quantity > remainingInventory) {
            toast.error(t("재고가 부족합니다"), {
                autoClose: 1000,
                position: toast.POSITION.BOTTOM_CENTER,
            });
            inputs.current[index].setValue(remainingInventory);
            quantity = remainingInventory;
        }

        setVariantQuantities(prev => ({
            ...prev,
            [variantId]: quantity
        }));

        setItems(prevItems => {
            const existingItemIndex = prevItems.findIndex(item => item.variant_id === variantId);
            if (existingItemIndex !== -1) {
                const updatedItems = [...prevItems];
                updatedItems[existingItemIndex] = {
                    ...updatedItems[existingItemIndex],
                    quantity: quantity
                };
                return updatedItems;
            } else {
                return [
                    ...prevItems,
                    { variant_id: variantId, quantity: quantity }
                ];
            }
        });
    };

    // 장바구니 추가 기능
    const onAddCartClick = async () => {
        if (!validateInventory(items)) {
            return;
        }

        const totalQuantity = Object.values(variantQuantities).reduce((acc, curr) => acc + curr, 0);
        if (totalQuantity <= 0) {
            toast.error(t("pleaseCheckTheQuantity"), {
                autoClose: 1000,
                position: toast.POSITION.BOTTOM_CENTER,
              });
            return;
        }

        const cartId = getCookie("_medusa_cart_id");
        if (userName && cartId) {
            try {
                await storeRequester.addItem(
                    cartId,
                    items.map((item) => ({
                        variant_id: item.variant_id,
                        quantity: item.quantity,
                        free: false,
                    }))
                );
                toast.success(t("putInAShoppingCart"), {
                    autoClose: 1000,
                    position: toast.POSITION.BOTTOM_CENTER,
                  });

                // cart 정보 갱신
                const { cart } = await storeRequester.getMyCart(regionId);
                setCartItems(cart?.items || []);

                setItems([]);
                setVariantQuantities({});
                modal.current.close();
            } catch (error) {
                toast.error(t("unexpected Error"), {
                    autoClose: 1000,
                    position: toast.POSITION.BOTTOM_CENTER,
                  });
            }
        } else {
            NiceModal.show("memberSignIn");
        }
    };

    // 바로구매 기능
    const onPurchaseClick = async () => {
        // 선택된 상품 중 품절된 상품이 있는지 확인
        const hasSoldOutItems = items.some(item => {
            const variant = product.variants?.find(v => v.id === item.variant_id) || product;
            return variant.inventory_quantity === 0 || variant.sold === true;
        });

        if (hasSoldOutItems) {
            toast.error(t("재고를 확인해주세요"), {
                autoClose: 1000,
                position: toast.POSITION.BOTTOM_CENTER,
            });
            return;
        }

        const totalQuantity = Object.values(variantQuantities).reduce((acc, curr) => acc + curr, 0);

        if (totalQuantity <= 0) {
            toast.error(t("pleaseCheckTheQuantity"), {
                autoClose: 1000,
                position: toast.POSITION.BOTTOM_CENTER,
            });
            return;
        }

        const cartId = getCookie("_medusa_cart_id");
        if (userName && cartId) {
            const currentItems = items.map(item => ({
                variant_id: item.variant_id,
                quantity: item.quantity,
                free: false,
            }));

            try {
                await storeRequester.addItem(cartId, currentItems);
                setItems([]);
                setVariantQuantities({});
                modal.current.close();
                navigate("/shopping/orderSummary");
            } catch (error) {
                toast.error('상품 추가에 실패했습니다.');
            }
        } else {
            NiceModal.show("memberSignIn");
        }
    };
    useEffect(() => {
        if (inputs.current.length > 0) {
            if (product.variants && product.variants.length > 0) {
                inputs.current.forEach((input, index) => {
                    const value = input?.getValue ? input.getValue() : null;

                    if (value !== 0) {
                        setItems(prevItems => {
                            const updatedItems = prevItems.map(item =>
                                item.index === index ? { ...item, quantity: value } : item
                            );
                            return updatedItems;
                        });

                        if (product?.variants[index]) {
                            setVariantQuantities(prev => ({
                                ...prev,
                                [product.variants[index].id]: value
                            }));
                        }
                    } else {
                        setItems(prevItems => prevItems.filter(item => item.index !== index));

                        if (product.variants[index]) {
                            setVariantQuantities(prev => {
                                const newState = { ...prev };
                                delete newState[product.variants[index].id];
                                return newState;
                            });
                        }
                    }
                });
            } else {
                inputs.current.forEach((input, index) => {
                    const value = input?.getValue ? input.getValue() : null;

                    if (value !== 0) {
                        setItems(prevItems => {
                            const updatedItems = prevItems.map(item =>
                                item.index === index ? { ...item, quantity: value } : item
                            );
                            return updatedItems;
                        });

                        if (product) {
                            setVariantQuantities(prev => ({
                                ...prev,
                                [product.id]: value
                            }));
                        }
                    } else {
                        setItems(prevItems => prevItems.filter(item => item.index !== index));

                        if (product) {
                            setVariantQuantities(prev => {
                                const newState = { ...prev };
                                delete newState[product.id];
                                return newState;
                            });
                        }
                    }
                });
            }
        }
    }, [inputs.current, isIncrease]);

    return (
        <MobileAddCartBase
            ref={modal}
            width={width}
            height={height}
            withHeader={withHeader}
            withFooter={withFooter}
            withCloseButton={withCloseButton}
            clickOutsideToClose={clickOutsideToClose}
            title={title}
            buttonText={buttonText}
            slideUp={true}
            onAddCartClick={onAddCartClick}
            onPurchaseClick={onPurchaseClick}
            totalPrice={calculateTotalPrice()}
        >
            <div className={style.modalContent}>
                <PaddingWrapper padding={"0px 0px"}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {product.variants && product.variants.length > 0 ?
                            product.variants.map((item, index) => (
                                <VerticalFlex
                                    key={item.id}
                                    gap={10}
                                    margin={'10px'}
                                    flexBasis={'43%'}
                                    maxWidth={'43%'}
                                    border={'2px solid grey'}
                                    borderRadius={"3px"}
                                >
                                    <FlexChild height={"max-content"}>
                                        {/* <img
                                            src={item.thumbnail}
                                            style={{
                                                width: "100%",
                                                opacity: (calculateRemainingInventory(item.id) === 0 || item.sold === true) ? 0.2 : 1,
                                            }}
                                            alt={item.title}
                                        /> */}
                                        {(calculateRemainingInventory(item.id) === 0 || item.sold === true) && (
                                            <div
                                                style={{
                                                    transform: "translate(30px,-100px)",
                                                    zIndex: 6,
                                                }}
                                            >
                                                <P size={20} weight={700} color={"#ca0000"}>
                                                    SOLD OUT
                                                </P>
                                            </div>
                                        )}
                                    </FlexChild>
                                    <FlexChild alignItems={"flex-start"} marginLeft={"20px"}>
                                        <P weight={700} size={22}>{item.title}</P>
                                    </FlexChild>
                                    <FlexChild marginLeft={"20px"} alignItems={"flex-start"}>
                                        <InputNumberMobile
                                            onChange={(value) => {
                                                handleInputChange(item.id, index, value);
                                                setIsIncrease(!isIncrease);
                                            }}
                                            ref={el => (inputs.current[index] = el)}
                                            min={0}
                                            max={item.sold ? 0 : calculateRemainingInventory(item.id)}
                                            required={true}
                                            height={50}
                                            isAllowed={!(calculateRemainingInventory(item.id) === 0 || item.sold === true)}
                                        />
                                    </FlexChild>
                                    <FlexChild height={"max-content"} alignItems={"flex-start"} marginLeft={"20px"}>
                                        <P weight={700} size={22}>{calculatePrice(item).toLocaleString()} </P>
                                    </FlexChild>
                                </VerticalFlex>
                            ))
                            :
                            <VerticalFlex
                                key={0}
                                gap={10}
                                margin={'10px'}
                                flexBasis={'43%'}
                                maxWidth={'43%'}
                                border={'2px solid grey'}
                                borderRadius={"3px"}
                            >
                                {/* 단일 상품 UI */}
                                <FlexChild height={"max-content"}>
                                    <img
                                        src={product.thumbnail}
                                        style={{
                                            width: "100%",
                                            opacity: (calculateRemainingInventory(product.id) === 0 || product.sold === true) ? 0.2 : 1
                                        }}
                                        alt={product.title}
                                    />
                                    {(calculateRemainingInventory(product.id) === 0 || product.sold === true) && (
                                        <div
                                            style={{
                                                transform: "translate(30px,-100px)",
                                                zIndex: 6,
                                            }}
                                        >
                                            <P size={20} weight={700} color={"#ca0000"}>
                                                SOLD OUT
                                            </P>
                                        </div>
                                    )}
                                </FlexChild>
                                <FlexChild alignItems={"flex-start"} marginLeft={"20px"}>
                                    <P weight={700} size={22}>{product.title}</P>
                                </FlexChild>
                                <FlexChild marginLeft={"20px"} alignItems={"flex-start"}>
                                    <InputNumberMobile
                                        onChange={(value) => {
                                            handleInputChange(product.id, 0, value);
                                            setIsIncrease(!isIncrease);
                                        }}
                                        ref={el => (inputs.current[0] = el)}
                                        min={0}
                                        max={product.sold ? 0 : calculateRemainingInventory(product.id)}
                                        required={true}
                                        height={50}
                                        isAllowed={!(calculateRemainingInventory(product.id) === 0 || product.sold === true)}
                                    />
                                </FlexChild>
                                <FlexChild height={"max-content"} alignItems={"flex-start"} marginLeft={"20px"}>
                                    <P weight={700} size={22}>{calculatePrice(product).toLocaleString()} </P>
                                </FlexChild>
                            </VerticalFlex>
                        }
                    </div>
                </PaddingWrapper>
            </div>
            <ToastContainer />
        </MobileAddCartBase>
    );
});

export default MobileAddCartModal;